import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import Button from 'components/ui-components/Button';
import Card from 'components/ui-components/Card';
import HookFormTextInput from 'components/cef_form/hook-form/HookFormTextInput';
import { HookFormProvider } from 'components/cef_form/hook-form/providers/HookFormProvider';
import { EMAIL_REGEX } from 'constants/constants';
import responsive from 'utils/responsive';
import toRem from 'utils/toRem';
import { useHTTP } from 'hooks/useRequest';
import ResetPasswordConfirmation from './confirmation';
import ErrorBox from './errorBox';

const RequestPasswordResetForm = ({ endpoint }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);

  const {
    control,
    watch,
    trigger,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      accountNumber: '',
      email: '',
      username: '',
    },
    mode: 'onChange',
  });

  const onSubmit = () => {
    const { email, username } = watch();

    // Set a custom error if both username and email are empty
    if (!email && !username) {
      setError('atLeastOne', {
        type: 'custom',
        message:
          'Please enter either the email address or username for your account',
      });
    } else {
      clearErrors('atLeastOne');
      setFormSubmitted(true);
    }
  };

  const accountNumber = watch('accountNumber');
  const email = watch('email');
  const username = watch('username');

  const RequestPasswordResetConfig = {
    method: 'POST',
    url: formSubmitted ? endpoint : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({
      password_reset: { account_number: accountNumber, email, username },
    }),
  };

  const { data, error, loading } = useHTTP(RequestPasswordResetConfig || null);

  useEffect(() => {
    if (!data && !error) return;

    setFormSuccess(false);

    const { success, error: responseError } = data || error;

    if (success) {
      setFormSuccess(true);
    } else if (responseError) {
      const {
        status,
        data: { errors: errorMessages },
      } = responseError;

      if (errorMessages)
        errorMessages.forEach(errorMessage => {
          setError('serverError', {
            type: status,
            message: errorMessage,
          });
        });
    }
  }, [data, error]);

  useEffect(() => {
    if (email || username) {
      clearErrors();
    }
  }, [email, username, clearErrors]);

  useEffect(() => {
    if (formSubmitted) setFormSubmitted(false);
  }, [formSubmitted]);

  return !formSuccess ? (
    <HookFormProvider
      control={control}
      trigger={trigger}
      watch={watch}
      setValue={setValue}
      setError={setError}
      clearErrors={clearErrors}
      errors={errors}>
      <FormWrapper className="margin-center width-100">
        <div className="ta-c mb-32">
          <h1 className="ff-rc fw-700 fs-30 l-fs-36 lh-1-2 l-lh-1 tc-main ta-c mb-12 mt-52">
            Reset Password
          </h1>
          <p className="fs-18 lh-1-25 tc-secondary ta-c m-fb-60">
            Please enter your account number and either your email address or
            registered username, and we&apos;ll send you a link to reset your
            password.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} data-datalayer="resetPassword">
          <FormCard variant="large" className="mb-32">
            <HookFormTextInput
              label="Account Number"
              description="This is the 8 digit number on your invoice"
              id="accountNumber"
              type="text"
              value={accountNumber}
              rules={{
                required: 'Please enter your account number',
                pattern: {
                  value: /^\d{8}$/,
                  message: 'Please enter a valid account number',
                },
              }}
            />

            <hr className="mt-8 mb-8" />

            <HookFormTextInput
              label="Email"
              id="email"
              type="email"
              value={email}
              rules={{
                pattern: {
                  value: EMAIL_REGEX,
                  message:
                    'Email must be in a specific format e.g. example@email.com',
                },
              }}
            />

            <p className="tc-secondary fs-14 fw-700 mb-0">OR</p>

            <HookFormTextInput
              label="Username"
              id="username"
              type="text"
              value={username}
            />

            {errors?.atLeastOne && (
              <ErrorBox text={errors.atLeastOne.message} />
            )}
            {errors?.serverError && (
              <ErrorBox text={errors.serverError.message} />
            )}

            <SubmitButton
              type="submit"
              id="submit-button"
              className="cef-btn cef-btn--green cef-btn--feature width-100 mt-24">
              {loading ? (
                <span className="cef-btn__loader bs-bb" />
              ) : (
                'Reset Password'
              )}
            </SubmitButton>
          </FormCard>
        </form>
      </FormWrapper>
    </HookFormProvider>
  ) : (
    <ResetPasswordConfirmation onResend={() => setFormSuccess(false)} />
  );
};

const FormWrapper = styled.div`
  ${responsive('desktopSmall')} {
    max-width: 650px;
  }
`;

const FormCard = styled(Card)`
  display: grid;
  gap: ${toRem(16)};
`;

const SubmitButton = styled(Button)`
  ${responsive('desktopSmall')} {
    max-width: 250px;
  }
`;

export default RequestPasswordResetForm;
